/**
 * Filtr „Obszar działania" na liście podwykonawców — czysta logika drzewka.
 *
 * Wybór trzymamy jako {województwo: [kody TERYT powiatów]}: klucz = zaznaczone
 * województwo, pusta lista = całe województwo (powiatów nie wybrano). Kilka
 * województw = KTÓREKOLWIEK z nich; powiaty zawężają tylko SWOJE województwo, a
 * kilka powiatów = KTÓRYKOLWIEK z nich — jak pozostałe filtry wielokrotnego wyboru.
 */

/** Wpis zasięgu z listy /contractors/ — `counties` puste = całe województwo. */
export interface ContractorArea {
  voivodeship: string;
  counties: string[];
}

export type AreaFilter = Record<string, string[]>;

/** Pseudo-województwo „Bez obszaru działania" — firmy bez żadnego wpisu zasięgu. */
export const NO_AREA_KEY = "__none__";

export function toggleVoivodeship(filter: AreaFilter, voivodeship: string): AreaFilter {
  const rest = { ...filter };
  if (voivodeship in rest) {
    // Odznaczenie województwa zabiera jego powiaty.
    delete rest[voivodeship];
    return rest;
  }
  return { ...rest, [voivodeship]: [] };
}

/** Przełącza powiat; odznaczenie ostatniego wraca do całego województwa. */
export function toggleCounty(filter: AreaFilter, voivodeship: string, teryt: string): AreaFilter {
  const current = filter[voivodeship] ?? [];
  const next = current.includes(teryt)
    ? current.filter((t) => t !== teryt)
    : [...current, teryt];
  return { ...filter, [voivodeship]: next };
}

/**
 * Czy firma działa w wybranym obszarze. Zaznaczone samo województwo — wystarczy
 * jakikolwiek wpis zasięgu w nim (zawężony też: to co najmniej jeden powiat). Z
 * wybranymi powiatami — zasięg musi objąć któryś z nich; wpis bez zawężenia
 * obejmuje całe województwo, więc każdy.
 */
export function matchesArea(area: ContractorArea[] | undefined, filter: AreaFilter): boolean {
  const entries = Object.entries(filter);
  if (entries.length === 0) return true;
  const rows = area ?? [];
  return entries.some(([voivodeship, counties]) => {
    if (voivodeship === NO_AREA_KEY) return rows.length === 0;
    const coverage = rows.find((a) => a.voivodeship === voivodeship);
    if (!coverage) return false;
    if (counties.length === 0 || coverage.counties.length === 0) return true;
    return counties.some((t) => coverage.counties.includes(t));
  });
}

/** Liczniki na przycisku filtra: zaznaczone województwa i wybrane powiaty. */
export function areaFilterCounts(filter: AreaFilter): { voivodeships: number; counties: number } {
  const lists = Object.values(filter);
  return {
    voivodeships: lists.length,
    counties: lists.reduce((sum, counties) => sum + counties.length, 0),
  };
}
